/**
 * BotManager - Zarządzanie turami botów (offline version)
 *
 * Offline port: socket.io (io) replaced with onStateUpdate callback.
 * game.save() / game.markModified() calls removed.
 * HeroService / ArtifactEffectProcessor removed (stubs).
 */

import GameEngine from './GameEngine.js';
import TurnManager from './TurnManager.js';
import BotAI from './BotAI.js';
import CardEffects from './CardEffects.js';
import ResourceManager from './ResourceManager.js';

const MAX_CONSECUTIVE_BOT_TURNS = 20;

class BotManager {
  /**
   * Execute a full bot turn
   * @param {Object} game - Game state
   * @param {Object} botPlayer - Bot player object
   * @param {Function} onStateUpdate - optional callback after state changes
   * @returns {Object} { gameEnded: bool }
   */
  static async executeBotTurn(game, botPlayer, onStateUpdate = null) {
    const difficulty = botPlayer.botDifficulty || botPlayer.difficulty || 'easy';
    let actionCount = 0;
    const MAX_ACTIONS = 20;

    // 1. Start phase
    await TurnManager.startPhase(game, botPlayer);

    // 2. Play cards
    try {
      const cardsToPlay = await BotAI.decideCardsToPlay(game, botPlayer, difficulty);
      for (const cardId of cardsToPlay) {
        if (actionCount >= MAX_ACTIONS) break;
        try {
          await GameEngine.playCard(game, botPlayer, cardId);
          actionCount++;
          await this.resolveAllPendingEffects(game, botPlayer, difficulty);
        } catch (err) {
          console.log(`[BOT_TURN] Error playing card ${cardId}:`, err.message);
        }
      }
    } catch (err) {
      console.error('[BOT_TURN] Error deciding cards:', err.message);
    }

    // 3. Main action loop (challenge + purchase)
    let didSomething = true;
    while (didSomething && actionCount < MAX_ACTIONS) {
      didSomething = false;

      // Challenge
      try {
        const challengeDecision = await BotAI.decideChallenge(game, botPlayer, difficulty);
        if (challengeDecision) {
          try {
            const path = challengeDecision.pathType;
            const cardIndex = challengeDecision.cardIndex;
            await GameEngine.challengePath(game, botPlayer, path, cardIndex);
            await this.resolveAllPendingEffects(game, botPlayer, difficulty);
            actionCount++;
            didSomething = true;
          } catch (err) {
            console.log('[BOT_TURN] Challenge error:', err.message);
          }
        }
      } catch (err) {
        console.error('[BOT_TURN] Error deciding challenge:', err.message);
      }

      // Purchase
      try {
        const purchaseDecision = await BotAI.decidePurchase(game, botPlayer, difficulty);
        if (purchaseDecision) {
          try {
            await GameEngine.purchaseCard(game, botPlayer, purchaseDecision.cardId);
            await this.resolveAllPendingEffects(game, botPlayer, difficulty);
            actionCount++;
            didSomething = true;
          } catch (err) {
            console.log('[BOT_TURN] Purchase error:', err.message);
          }
        }
      } catch (err) {
        console.error('[BOT_TURN] Error deciding purchase:', err.message);
      }

      if (!didSomething) break;
    }

    // 4. Transfer resources to bank
    try {
      ResourceManager.transferAllToBank(botPlayer, game);
    } catch (err) {
      console.log('[BOT_TURN] Banking error:', err.message);
    }

    // 5. End turn
    console.log(`[BOT_TURN] Bot ${botPlayer.username} ended turn: ${actionCount} actions, fame: ${botPlayer.fame}`);
    try {
      const endResult = await TurnManager.endPhaseWithoutRefill(game, botPlayer);
      return endResult || { gameEnded: false };
    } catch (err) {
      console.error('[BOT_TURN] End turn error:', err.message);
      return { gameEnded: false };
    }
  }

  /**
   * Resolve all pending effects for a bot player
   */
  static async resolveAllPendingEffects(game, botPlayer, difficulty) {
    let iterations = 0;
    const MAX_ITERATIONS = 20;

    while (botPlayer.pendingEffects && botPlayer.pendingEffects.length > 0 && iterations < MAX_ITERATIONS) {
      iterations++;
      const pendingEffect = botPlayer.pendingEffects[0];

      try {
        const choice = await BotAI.resolvePendingEffect(pendingEffect, game, botPlayer, difficulty);

        let context;
        if (choice.cardIds !== undefined) {
          context = { cardIds: choice.cardIds };
        } else if (choice.cardId !== undefined) {
          context = { cardId: choice.cardId };
        } else {
          context = { choice: choice.option || choice.choice || 'skip' };
        }

        if (pendingEffect.type === 'LEGENDARY_ARTIFACT_ACQUISITION') {
          context = { choice: choice.option || choice.choice || 'equip' };
        }

        if (pendingEffect.type === 'ARTIFACT_STAT_BONUS_CHOICE') {
          const chosenResource = context.choice;
          const value = pendingEffect[chosenResource] || 1;
          ResourceManager.addToPool(botPlayer, { [chosenResource]: value });
          botPlayer.pendingEffects.splice(0, 1);
          continue;
        }

        await CardEffects.applyEffect(game, botPlayer, pendingEffect, context);
        botPlayer.pendingEffects.splice(0, 1);

        // Re-add multi-destroy if value > 1
        const multiDestroyEffects = [
          'DESTROY_FROM_HAND', 'DESTROY_FROM_DISCARD', 'DESTROY_FROM_HAND_OR_DISCARD'
        ];
        if (multiDestroyEffects.includes(pendingEffect.type) && pendingEffect.value > 1) {
          const remainingValue = pendingEffect.value - 1;
          const newOptions = await GameEngine.getValidOptionsForEffect(game, botPlayer, pendingEffect.type);
          if (newOptions && newOptions.length > 0) {
            botPlayer.pendingEffects.push({
              type: pendingEffect.type,
              value: remainingValue,
              source: pendingEffect.source,
              cardId: pendingEffect.cardId,
              options: newOptions,
              description: pendingEffect.description
            });
          }
        }
      } catch (err) {
        console.error(`[BOT_RESOLVE] Error resolving ${pendingEffect.type}:`, err.message);
        botPlayer.pendingEffects.splice(0, 1);
      }
    }

    if (iterations >= MAX_ITERATIONS) {
      console.warn('[BOT_RESOLVE] Max iterations reached, clearing pending effects');
      botPlayer.pendingEffects = [];
    }
  }

  /**
   * Run all consecutive bot turns until it's a human's turn
   */
  static async executeBotTurnsLoop(game, onStateUpdate = null) {
    let consecutiveBotTurns = 0;
    let currentPlayer = game.getCurrentPlayer();

    while (currentPlayer.isBot && !game.gameEnded && consecutiveBotTurns < MAX_CONSECUTIVE_BOT_TURNS) {
      consecutiveBotTurns++;
      console.log(`[BOT_LOOP] Bot turn #${consecutiveBotTurns}: ${currentPlayer.username}`);

      const endResult = await this.executeBotTurn(game, currentPlayer, onStateUpdate);

      if (endResult && endResult.gameEnded) {
        console.log('[BOT_LOOP] Game ended during bot turn');
        return { gameEnded: true };
      }

      // Refill paths and finalize transition
      if (game.refillMarketplace) game.refillMarketplace();
      if (game.refillPathOfCourage) game.refillPathOfCourage();
      if (game.refillPathOfWisdom) game.refillPathOfWisdom();

      TurnManager.nextPlayer(game);
      const nextPlayer = game.getCurrentPlayer();
      await TurnManager.startPhase(game, nextPlayer);

      if (onStateUpdate) onStateUpdate(game);

      currentPlayer = nextPlayer;
    }

    return { gameEnded: false };
  }
}

export default BotManager;
